import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, AppBar, Toolbar, Typography, Card, CardContent, Stack, Chip } from '@mui/material';
import { Search as SearchIcon, Restore as RestoreIcon, Clear as ClearIcon } from '@mui/icons-material';
import Swal from "sweetalert2";
import MainContentContainer from "./../MainContentContainer/MainContentContainer";
import { fetchUserStreak, restoreUserStreak } from "./../../actions/userStreakActions";

const UserStreakPage = () => {
  const dispatch = useDispatch();
  const user_streak = useSelector(state => state.user_streak.user_streak);
  const restored_dates = useSelector(state => state.user_streak.restored_dates);

  const [user_streak_email, setUserStreakEmail] = useState('');
  const [streak_days, setStreakDays] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [missingDates, setMissingDates] = useState([]);

  useEffect(() => {
    if (user_streak) {
      setMissingDates(calculateMissingDates(user_streak, streak_days));
    }
  }, [user_streak, streak_days]);

  const calculateMissingDates = (user_streak, streak_days) => {
    return Array.from(new Set(user_streak.slice(0, streak_days).filter(item => item.streak === 0).map(item => item.date)));
  };

  const handleFetchData = () => {
    setHasSearched(true);
    dispatch(fetchUserStreak({
      user_email: user_streak_email,
      days: streak_days
    }));
  };

  const handleRestoreStreak = () => {
    if (!hasAnySessions) {
      setOpenDialog(false);
      Swal.fire({
        icon: 'info',
        title: 'No sessions found',
        text: 'This user has no completed sessions to restore a streak from.',
      });
      return;
    }
    dispatch(restoreUserStreak({
      user_email: user_streak_email,
      days: streak_days
    }));
    setOpenDialog(false);
  };

  const handleClear = () => {
    setUserStreakEmail('');
    setStreakDays(10);
    setMissingDates([]);
    setHasSearched(false);
    dispatch({ type: 'CLEAR_USER_STREAK' });
  };

  const streakData = Array.isArray(user_streak) ? user_streak : [];
  const hasAnySessions = streakData.some(item => item.streak > 0);
  const [hasSearched, setHasSearched] = useState(false);
  let currentStreak = 0;
  let maxStreak = 0;

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            User Streaks
          </Typography>
          {(user_streak_email || streakData.length > 0) && (
            <Button
              color="inherit"
              onClick={handleClear}
              startIcon={<ClearIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Clear
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, mt: 3 }}>
        <Card sx={{ width: { md: '50%' } }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" gutterBottom>
                Search User Streak
              </Typography>
              <TextField
                label="User Email"
                variant="outlined"
                value={user_streak_email}
                onChange={(e) => setUserStreakEmail(e.target.value)}
                fullWidth
                placeholder="Enter user email"
              />
              <TextField
                label="Number of Days"
                variant="outlined"
                type="number"
                value={streak_days}
                onChange={(e) => setStreakDays(e.target.value)}
                fullWidth
                placeholder="Enter number of days"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFetchData}
                  startIcon={<SearchIcon />}
                  fullWidth
                >
                  Search Streak
                </Button>
                {streakData.length > 0 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setOpenDialog(true)}
                    startIcon={<RestoreIcon />}
                    fullWidth
                  >
                    Restore Streak
                  </Button>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {missingDates.length > 0 && (
          <Card sx={{ width: { md: '50%' } }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Missing Dates
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {missingDates.map(date => (
                  <Chip
                    key={date}
                    label={date}
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {hasSearched && streakData.length === 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" color="text.secondary">
            No streak data found for this user
          </Typography>
        </Box>
      )}

      {streakData.length > 0 && (
        <TableContainer sx={{ marginTop: 3 }} component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Streak</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {streakData.map((item) => {
                const isConsecutive = item.streak > 0;
                if (isConsecutive) {
                  currentStreak++;
                  maxStreak = Math.max(currentStreak, maxStreak);
                } else {
                  currentStreak = 0;
                }

                return (
                  <TableRow key={item.date} style={{ backgroundColor: isConsecutive ? 'lightgreen' : 'lightcoral' }}>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>{item.streak}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Restore"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to restore {user_streak_email}'s streak?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRestoreStreak} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </MainContentContainer>
  );
};

export default UserStreakPage;
